import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import atatus from 'atatus';
import useAccounts from 'context/useAccounts';
import { format, subDays, subMonths } from 'date-fns';
import useFormFields from 'hooks/useFormFields';
import useGTM from 'hooks/useGTM';

import { ReactComponent as NavigationArrow } from 'assets/icons/navigation-arrow.svg';
import DateRangePicker from 'components/dateRangePicker/DateRangePicker';
import FocusableHeader from 'components/focusableheader/FocusableHeader';
import Link from 'components/link/Link';
import Spinner from 'components/spinner/Spinner';
import { PrimaryButtonSto, TextButtonSto } from 'components/sto';
import TransactionList from 'components/transaction/TransactionList';
import { AppRoutes } from 'routes';
import { getTransactions } from 'services/AccountService';
import { formatAccountNumber } from 'utils/Formatter';

import DATE_RANGE_ERROR_MAPPING from './dateRangeErrorMapping';

const HistoryPage = () => {
    const {
        activeAccount: { accountNumber, merchantName },
    } = useAccounts();
    const { stepChange } = useGTM();

    const { formFields, setFormField } = useFormFields({
        endDate: { value: new Date() },
        startDate: { value: subDays(new Date(), 30) },
    });

    const { endDate, startDate } = formFields;
    const [nextTxnCode, setNextTxnCode] = useState('');
    const [fetchType, setFetchType] = useState('');
    const [fetchErrorType, setFetchErrorType] = useState('');
    const [transactions, setTransactions] = useState(null);

    useEffect(() => {
        stepChange();
        fetchTransactions('init', dateRange);
    }, []);

    const dateRange = useMemo(() => [startDate.value, endDate.value], [startDate, endDate]);

    const invalidDates = useMemo(() => !!startDate.errorMessage || !!endDate.errorMessage, [startDate, endDate]);

    const hasMoreTransactions = useMemo(() => transactions && transactions.length > 0 && nextTxnCode, [
        transactions,
        nextTxnCode,
    ]);

    const handleDateChange = (dates) => {
        for (const name in dates) {
            const error = dates[name].errorMessage;
            dates[name].errorMessage = DATE_RANGE_ERROR_MAPPING[name][error];
        }

        setTransactions(null);
        setNextTxnCode('');
        setFetchErrorType('');
        setFormField('startDate', dates['startDate']);
        setFormField('endDate', dates['endDate']);
    };

    const handleReset = () => {
        const newStartDate = subDays(new Date(), 30);
        const newEndDate = new Date();

        setTransactions(null);
        setNextTxnCode('');
        setFormField('startDate', {
            errorMessage: null,
            isValid: true,
            value: newStartDate,
        });
        setFormField('endDate', {
            errorMessage: null,
            isValid: true,
            value: newEndDate,
        });
        fetchTransactions('init', [newStartDate, newEndDate]);
    };

    const fetchTransactions = async (type, dates, txnCode) => {
        setFetchErrorType('');

        if (type === 'init') {
            setTransactions(null);
        }

        const queryParams = {
            beginningPostingDate: format(dates[0], 'yyyy-MM-dd'),
            endingPostingDate: format(dates[1], 'yyyy-MM-dd'),
            numberOfTransactions: 50,
        };

        if (txnCode) {
            queryParams.nextTransactionCode = txnCode;
        }

        atatus.startTransaction('GetTransactions');

        try {
            setFetchType(type);

            const { additionalTransactions, nextTransactionCode, transactions: newTxns } = await getTransactions(
                accountNumber,
                queryParams
            );

            setTransactions((oldTxns) => (!oldTxns ? newTxns : oldTxns.concat(newTxns)));

            additionalTransactions ? setNextTxnCode(nextTransactionCode) : setNextTxnCode('');
            atatus.endTransaction('GetTransactions');
        } catch (e) {
            setFetchErrorType(type);
            atatus.failTransaction('GetTransactions');
        } finally {
            setFetchType('');
        }
    };
    return (
        <section className="transaction-page grey-banner">
            <div className="transaction-page-heading">
                <Link aria-label="return to accounts page" to={AppRoutes.accounts}>
                    <NavigationArrow aria-label="back" className="navigation-arrow" />
                </Link>
                <FocusableHeader className="transaction-page-title">
                    <FormattedMessage id="transaction.history.subtitle" />
                </FocusableHeader>
                <hr aria-hidden className="fs-bar orange" />
            </div>
            <div className="transaction-page-subtitle">
                <h5 className="merchantName">{merchantName}</h5>
                <p className="account-number">{formatAccountNumber(accountNumber, true)}</p>
            </div>
            <div className="transaction-page-content box-wrapper">
                <p className="date-picker-label">
                    <FormattedMessage id="date.picker.label" />
                </p>
                <p className="date-picker-sub-label">
                    <FormattedMessage id="date.picker.sublabel" />
                </p>
                <DateRangePicker
                    className="date-range-picker"
                    endDate={endDate.value}
                    endDateErrorMessage={endDate.errorMessage}
                    maxDate={new Date()}
                    minDate={subMonths(new Date(), 24)}
                    onChange={handleDateChange}
                    startDate={startDate.value}
                    startDateErrorMessage={startDate.errorMessage}
                />
                <div className="date-range-error">
                    <p>
                        {(startDate.errorMessage || endDate.errorMessage) && (
                            <FormattedMessage id={startDate.errorMessage || endDate.errorMessage} />
                        )}
                    </p>
                </div>
                <div className="btns-wrapper">
                    <TextButtonSto labelKey="transaction.history.reset.dates" onClick={handleReset} size="small" />
                    <PrimaryButtonSto
                        disabled={invalidDates}
                        labelKey="transaction.history.select.dates"
                        onClick={() => fetchTransactions('init', dateRange)}
                        size="small"
                        type="button"
                    />
                </div>
                <TransactionList
                    error={fetchErrorType === 'init'}
                    isFetching={fetchType === 'init'}
                    transactions={transactions}
                />
                {hasMoreTransactions && (
                    <div className="load-more-container">
                        <TextButtonSto
                            className="load-more-btn"
                            labelKey="transaction.page.load.more"
                            onClick={() => fetchTransactions('loadMore', dateRange, nextTxnCode)}
                            size="small"
                        />
                        {fetchType === 'loadMore' && (
                            <div className="load-more-spinner-wrapper">
                                <Spinner />
                            </div>
                        )}
                        {fetchErrorType === 'loadMore' && (
                            <div className="load-more-error">
                                <p id="gtm-error-message-transaction-load-more">
                                    <FormattedMessage id="transaction.page.load.more.api.error" />
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default HistoryPage;
